// Pure formatters shared across the cycle-16 chat-depth primitives.
// All formatters take typed inputs and return strings — they never log,
// never read globals beyond `Intl`, and stay deterministic so snapshot
// tests are stable across CI hosts.

const TIME_FORMAT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const SHORT_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

export function formatClockTime(date: Date): string {
  return TIME_FORMAT.format(date);
}

export function formatShortDate(date: Date): string {
  return SHORT_DATE_FORMAT.format(date);
}

export function formatEditedTimestamp(date: Date): string {
  return `Edited at ${formatClockTime(date)}`;
}

const DURATION_PAD = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

// `mm:ss` for durations under one hour, `h:mm:ss` otherwise. Voice
// messages that exceed an hour are intentionally handled — the cap is
// a host concern.
export function formatDuration(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${DURATION_PAD(minutes)}:${DURATION_PAD(seconds)}`;
  }
  return `${minutes}:${DURATION_PAD(seconds)}`;
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

// IEC-style byte formatter. Returns a single string with one decimal of
// precision above the kilobyte threshold so the doc card can render a
// stable "1.4 MB" without re-allocating per render.
export function formatBytes(byteSize: number): string {
  if (!Number.isFinite(byteSize) || byteSize < 0) {
    return '0 B';
  }
  if (byteSize === 0) {
    return '0 B';
  }
  const exponent = Math.min(
    BYTE_UNITS.length - 1,
    Math.floor(Math.log(byteSize) / Math.log(1024)),
  );
  const value = byteSize / 1024 ** exponent;
  const unit = BYTE_UNITS[exponent] ?? 'B';
  if (exponent === 0) {
    return `${Math.round(value)} ${unit}`;
  }
  return `${value.toFixed(1)} ${unit}`;
}

// Snap an `0..1` progress value to a 33ms grid (≈ 30 fps) keyed on a
// total duration. Used by `VoiceMessageBubble` + `RecordingPlayback` so
// per-frame redraws coalesce to one bar update at the throttle budget.
export function snapProgressToFrame(progress: number, totalMs: number): number {
  if (totalMs <= 0) {
    return 0;
  }
  const clamped = Math.min(1, Math.max(0, progress));
  const totalFrames = Math.max(1, Math.round(totalMs / 33));
  const frame = Math.round(clamped * totalFrames);
  return frame / totalFrames;
}

// `aspectRatio` clamp matches the iOS spec's 0.5..1.6 range so very tall
// portraits and very wide panoramas don't push the message out of the
// timeline.
export function clampAspectRatio(aspectRatio: number): number {
  if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    return 1;
  }
  return Math.min(1.6, Math.max(0.5, aspectRatio));
}
